import '@livekit/components-styles';
import { Room } from 'livekit-client';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RoomAudioRenderer, RoomContext } from '@livekit/components-react';
import {
  Button,
  Modal,
  analyticsClient,
} from '@spotted-zebra-uk/ui-components';
import { AvatarVideoFeed } from './AvatarVideoFeed';
import { AvatarControls } from './AvatarControls';
import { AgentStatusBadge } from '../AgentStatusBadge';
import { ParticipantFeed } from '../ParticipantFeed/ParticipantFeed';
import styles from './ConferenceRoomWithAvatars.module.scss';
import { HEAP_ANALYTICS_EVENTS } from '@/constants/analytics';
import { EndCallConfig } from '../ConferenceLobby/ConferenceLobby';

interface ConferenceRoomWithAvatarsProps {
  room: Room;
  onEndCall: () => void;
  endCallConfig?: EndCallConfig;
}

export function ConferenceRoomWithAvatars({
  room,
  onEndCall,
  endCallConfig,
}: ConferenceRoomWithAvatarsProps) {
  const { t } = useTranslation();
  const resolvedEndCallConfig: EndCallConfig = endCallConfig ?? {
    title: t('aiInterview.endInterview.title'),
    message: t('aiInterview.endInterview.message'),
    confirmLabel: t('aiInterview.endInterview.confirm'),
  };
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [showEndCallModal, setShowEndCallModal] = useState(false);

  const handleEndCallClick = useCallback(() => {
    setShowEndCallModal(true);

    analyticsClient.trackEvent({
      name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_CONFERENCE_ROOM_END_CALL_BUTTON_CLICK,
    });
  }, []);

  const handleCancelEndCall = useCallback(() => {
    setShowEndCallModal(false);

    analyticsClient.trackEvent({
      name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_CONFERENCE_ROOM_END_CALL_CANCEL_MODAL_CLICK,
    });
  }, []);

  const handleConfirmEndCall = useCallback(async () => {
    setShowEndCallModal(false);
    setIsDisconnecting(true);

    // Stop all local tracks to release camera/microphone
    room.localParticipant.trackPublications.forEach(publication => {
      if (publication.track) {
        publication.track.stop();
        // Also stop the underlying MediaStreamTrack if available
        if (publication.track.mediaStreamTrack) {
          publication.track.mediaStreamTrack.stop();
        }
      }
    });

    analyticsClient.trackEvent({
      name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_CONFERENCE_ROOM_END_CALL_CONFIRM_MODAL_CLICK,
    });

    // Call onEndCall which triggers leaveConference
    onEndCall();
  }, [room, onEndCall]);

  useEffect(() => {
    analyticsClient.trackEvent({
      name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_CONFERENCE_ROOM_PAGE_VISIT,
    });
  }, []);

  // Prevent page scrollbar while conference room is active
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Critical: Cleanup media streams when component unmounts
  // This ensures camera/microphone are released even if user navigates away
  useEffect(() => {
    return () => {
      room.localParticipant.trackPublications.forEach(publication => {
        if (publication.track) {
          // Stop the LiveKit track
          publication.track.stop();
          // Also stop the underlying MediaStreamTrack to ensure hardware is released
          if (publication.track.mediaStreamTrack) {
            publication.track.mediaStreamTrack.stop();
          }
        }
      });
    };
  }, [room]);

  return (
    <RoomContext.Provider value={room}>
      <RoomAudioRenderer />
      <div className={styles.conferenceRoomWithAvatars}>
        <div data-lk-theme="default" className={styles.conferenceContainer}>
          <AvatarVideoFeed />
          <ParticipantFeed />
          <div className={styles.statusBadgeContainer}>
            <AgentStatusBadge />
          </div>
          <AvatarControls
            onEndCall={handleEndCallClick}
            isDisconnecting={isDisconnecting}
          />
        </div>
      </div>

      {/* @ts-ignore */}
      <Modal
        isOpen={showEndCallModal}
        onClose={handleCancelEndCall}
        className={styles.endCallModal}
      >
        <h3 className={styles.endCallModalTitle}>{resolvedEndCallConfig.title}</h3>
        <p className={styles.endCallModalText}>{resolvedEndCallConfig.message}</p>
        <div className={styles.endCallModalButtons}>
          <Button variant="tertiary" onClick={handleCancelEndCall}>
            {t('common.cancel')}
          </Button>
          <Button variant="negativePrimary" onClick={handleConfirmEndCall}>
            {resolvedEndCallConfig.confirmLabel}
          </Button>
        </div>
      </Modal>
    </RoomContext.Provider>
  );
}
